import { useQueryClient } from "@tanstack/react-query";

import {
  applyEntityPreviewAsset,
  applyEntityPreviewUpdate,
  isEntityPreviewUpdateMessage,
  lookupEntityPreviewAsset,
} from "./entityPreviewCache";

import { useChannel } from "@/components/Messaging/useChannel";
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";

export const useEntityPreviewUpdates = (workspaceId: number | null, enabled = true) => {
  const { user } = useUser();
  const auth = useAuth();
  const queryClient = useQueryClient();

  useChannel(
    `user:${user.id}`,
    WEBSOCKET_MESSAGE_NAME.ENTITY_PREVIEW_UPDATED,
    message => {
      let parsedMessage: unknown;
      try {
        parsedMessage = JSON.parse(String(message.data));
      } catch {
        return;
      }

      if (!isEntityPreviewUpdateMessage(parsedMessage)) {
        return;
      }

      if (workspaceId !== null && parsedMessage.workspace_id !== workspaceId) {
        return;
      }

      applyEntityPreviewUpdate(queryClient, parsedMessage);

      if (parsedMessage.status !== "ready" || parsedMessage.image_url || !auth?.userToken) {
        return;
      }

      void lookupEntityPreviewAsset({
        workspaceId: parsedMessage.workspace_id,
        entityType: parsedMessage.entity_type,
        entityId: parsedMessage.entity_id,
        userToken: auth.userToken,
        requestMissing: false,
      })
        .then(preview => {
          if (!preview) {
            return;
          }

          applyEntityPreviewAsset(queryClient, {
            workspaceId: parsedMessage.workspace_id,
            entityType: parsedMessage.entity_type,
            entityId: parsedMessage.entity_id,
            preview,
          });
        })
        .catch(() => undefined);
    },
    enabled,
  );
};
